/** 从 Form 读取 UI 态字段（不组装 headGroupSpec / workerGroupSpecs） */
import type { FormInstance } from '@arco-design/web-react/es/Form';

import type { MaasSharedFormValues } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { readCommonEnvFromForm, resolveCommonEnvFromValues } from '@/delivery-type-plugin/capabilities/maas-init/utils/commonEnv';
import { parseFormJsonField } from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import { PRIMARY_DATA_VOLUME_FIELD } from '@/delivery-type-plugin/capabilities/maas-node-groups/masterGroupForm';
import {
  INFERENCE_SERVICE_FIELD,
  INFERENCE_SYSTEM_FIELD,
  MASTER_NODES_FIELD,
  MASTER_SYSTEM_DISK_FIELD,
  WORKER_NODES_FIELD,
  WORKER_SYSTEM_DISK_FIELD,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFieldNames';
import {
  IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD,
  IMAGE_CONTAINER_DEPLOY_STARTUP_FIELD,
} from '@/delivery-type-plugin/capabilities/maas-system-config/containerDeployConfigFields';

/** 读取 MaaS 创建表单 UI 字段（不含组 spec 组装） */
export function readMaasFormValues(form: FormInstance): MaasSharedFormValues {
  const values = form.getFields() as MaasSharedFormValues;
  const primaryDataVolumeJson = form.getFieldValue(PRIMARY_DATA_VOLUME_FIELD) as string | undefined;

  const commonEnv =
    readCommonEnvFromForm(form) ??
    resolveCommonEnvFromValues({
      commonEnv: values.commonEnv as MaasSharedFormValues['commonEnv'],
      environment: values.environment as MaasSharedFormValues['environment'],
    });

  return {
    ...values,
    ...(commonEnv ? { commonEnv } : {}),
    runMode: (form.getFieldValue('runMode') ?? values.runMode) as MaasSharedFormValues['runMode'],
    masterNodes: form.getFieldValue(MASTER_NODES_FIELD) as string | undefined,
    workerNodes: form.getFieldValue(WORKER_NODES_FIELD) as string | undefined,
    /** 叶子字段注册时 getFieldValue('_inferenceSystem') 可能为空，须回退 getFields() 聚合值 */
    _inferenceSystem: parseFormJsonField<Record<string, unknown>>(form.getFieldValue(INFERENCE_SYSTEM_FIELD) ?? values._inferenceSystem),
    _inferenceService: parseFormJsonField(form.getFieldValue(INFERENCE_SERVICE_FIELD) ?? values._inferenceService),
    _imageContainerDeployStartup: parseFormJsonField(
      form.getFieldValue(IMAGE_CONTAINER_DEPLOY_STARTUP_FIELD) ?? values._imageContainerDeployStartup,
    ),
    _imageContainerDeployInstanceScheduling: parseFormJsonField(
      form.getFieldValue(IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD) ?? values._imageContainerDeployInstanceScheduling,
    ),
    'master.systemDiskSize': form.getFieldValue(MASTER_SYSTEM_DISK_FIELD) as number | undefined,
    'worker.systemDiskSize': form.getFieldValue(WORKER_SYSTEM_DISK_FIELD) as number | undefined,
    ...(primaryDataVolumeJson != null && primaryDataVolumeJson !== '' ? { _primaryDataVolume: primaryDataVolumeJson } : {}),
  };
}
